import { useCallback, useEffect, useState, type FormEvent } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  APIProvider,
  ControlPosition,
  Map,
  MapControl,
  Marker,
  useMap,
  useMapsLibrary,
} from '@vis.gl/react-google-maps'
import { useAutocompleteSuggestions } from '@/hook/useAutocompoeteSuggestions'
import { useDebounce } from '@/hook/useDebounce'
import { cn } from '@/lib/styled'
import { Button } from '../ui/button'
import { X } from 'lucide-react'
import toast from 'react-hot-toast'

const mapStyles = [
  {
    featureType: 'poi', // All points of interest
    stylers: [{ visibility: 'off' }],
  },
]

interface Place {
  place: google.maps.places.Place
  address: string
  lat: number
  lng: number
}

const AutocompleteCustom = ({ search, setPredictions }: any) => {
  const { suggestions } = useAutocompleteSuggestions(search)

  useEffect(() => {
    if (suggestions) {
      setPredictions(suggestions)
    }
  }, [suggestions])

  return null
}

const AutocompleteResult = ({
  place,
}: {
  place: google.maps.places.Place | null
}) => {
  const map = useMap()
  // adjust the viewport of the map when the place is changed
  useEffect(() => {
    if (!map || !place) return
    if (place.viewport) map.fitBounds(place.viewport)
  }, [map, place])

  if (!place) return null

  return <Marker position={place.location} />
}

const LocationPicker = (props: {
  location: {
    address: string
    lat: number
    lng: number
  }
  show: [boolean, (open: boolean) => void]
  onSave: (place: {
    place: google.maps.places.Place
    address: string
    lat: number
    lng: number
  }) => void
}) => {
  const places = useMapsLibrary('places')
  const { location, show, onSave } = props // destructuring props
  const [open, setOpen] = show // destructuring show state

  const [searchValue, setSearchValue] = useState<string>('')
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompleteSuggestion[] | []
  >([])

  const [selectedPlace, setSelectedPlace] = useState<
    | Place
    | {
        place?: google.maps.places.Place
        address: string
        lat: number
        lng: number
      }
    | undefined
  >(undefined)

  const handleInput = useCallback((event: FormEvent<HTMLInputElement>) => {
    setSearchValue((event.target as HTMLInputElement).value)
  }, [])

  const debouncedValue = useDebounce(searchValue, 500)

  const handleSuggestionClick = useCallback(
    async (suggestion: google.maps.places.AutocompleteSuggestion) => {
      if (!suggestion.placePrediction) return

      let place = suggestion.placePrediction.toPlace()

      await place.fetchFields({
        fields: [
          'viewport',
          'location',
          'svgIconMaskURI',
          'iconBackgroundColor',
        ],
      })

      setSearchValue(suggestion.placePrediction?.text.text || '')
      setPredictions([])

      // calling fetchFields invalidates the session-token, so we now have to call
      // resetSession() so a new one gets created for further search

      const lat = place.location?.lat()
      const lng = place.location?.lng()

      setSelectedPlace({
        place,
        address: suggestion.placePrediction?.text.text,
        lat: lat as number,
        lng: lng as number,
      })
    },
    [places, setSelectedPlace]
  )

  useEffect(() => {
    setSearchValue(location.address)
    setSelectedPlace({
      place: {
        location: {
          lat: location.lat,
          lng: location.lng,
        },
        viewport: null,
      } as unknown as google.maps.places.Place,
      address: location.address,
      lat: location.lat,
      lng: location.lng,
    })
  }, [location])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent position='bottom' className='w-full rounded-t-md bg-white'>
        <DialogHeader>
          <DialogTitle>Location</DialogTitle>
        </DialogHeader>
        <DialogDescription asChild>
          <div className='relative'>
            <input
              value={searchValue}
              onChange={(event) => handleInput(event)}
              // onFocus={() => console.log('focus', true)}
              // onClick={(event) => {
              //   console.log('Input clicked:', event.currentTarget)
              // }}
              
              placeholder='Search for a place'
              className={cn(
                'w-full relative border p-3 rounded-md focus:outline-2 focus:outline-primary',
                predictions.length > 0 && 'rounded-b-none'
              )}
            />
            <X
              className='absolute w-4 right-2 top-1/2 -translate-y-1/2 text-gray-500'
              onClick={() => {
                setSearchValue('')
                setSelectedPlace(undefined)
              }}
            />

            {predictions.length > 0 && (
              <ul className='z-50 absolute top-full left-0 w-full bg-white border border-gray-300 rounded-b-md'>
                {predictions.map(
                  (
                    suggestion: google.maps.places.AutocompleteSuggestion,
                    index: number
                  ) => {
                    return (
                      <li
                        key={index}
                        className={cn(
                          'p-2 hover:bg-gray-100 cursor-pointer',
                          predictions.length - 1 !== index && 'border-b'
                        )}
                        onClick={() => handleSuggestionClick(suggestion)}
                      >
                        {suggestion.placePrediction?.text.text}
                      </li>
                    )
                  }
                )}
              </ul>
            )}
          </div>
        </DialogDescription>
        <div className='h-[300px] w-full rounded-lg overflow-hidden mb-4'>
          {/* starts:: map */}
          <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
            <Map
              defaultCenter={{
                lat: selectedPlace?.lat as number,
                lng: selectedPlace?.lng as number,
              }}
              defaultZoom={17}
              gestureHandling={'greedy'}
              disableDefaultUI={true}
              styles={mapStyles}
              clickableIcons={false}
            >
              <MapControl position={ControlPosition.TOP_LEFT}>
                <AutocompleteCustom
                  search={debouncedValue}
                  setPredictions={setPredictions}
                />
              </MapControl>
              <AutocompleteResult
                place={selectedPlace?.place as google.maps.places.Place}
              />
            </Map>
          </APIProvider>
          {/* ends:: map */}
        </div>
        <DialogFooter>
          <div className='w-full grid grid-cols-2 gap-2'>
            <Button
              variant='outline'
              className='w-full'
              onClick={() => {
                if (!searchValue && !selectedPlace) setOpen(false)
                else {
                  setSearchValue('')
                  setSelectedPlace(undefined)
                }
              }}
            >
              {selectedPlace ? 'Clear' : 'Close'}
            </Button>
            <Button
              variant='primary'
              className='w-full'
              onClick={() => {
                if (!selectedPlace)
                  toast.error('Please select a location first')
                else onSave(selectedPlace as Place)
              }}
            >
              Save
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

export default LocationPicker
